import React, { useMemo, useState } from 'react';
import { Button, Col, Dropdown, Input, Row } from 'antd';
import { ItemType } from 'antd/lib/menu/hooks/useItems';
import { useResponsive } from '@hooks/useResponsive';
import { ReactComponent as CrossSvg } from '@assets/icons/cross.svg';
import { ReactComponent as SearchSvg } from '@assets/icons/search.svg';
import { ReactComponent as DownArrowSvg } from '@assets/icons/arrow-down.svg';

import type { TextModeType } from '@pages/MediaViewer/components/TranscriptDrawer/TranscriptDrawer';

type HeadExtraProps = {
  searchValue: string;
  setSearchValue: (value: string) => void;
  textMode: TextModeType;
  setTextMode: (value: TextModeType) => void;
};

export default function HeadExtra({
  searchValue,
  setSearchValue,
  textMode,
  setTextMode
}: HeadExtraProps) {
  const { isMdWidth } = useResponsive();
  const [isShowSearch, setIsShowSearch] = useState(false);

  const textModeOptions = useMemo(() => {
    const items: ItemType[] = [
      {
        key: 'transcript',
        label: 'Transcript'
      },
      {
        key: 'captions',
        label: 'Captions'
      }
    ];
    if (isMdWidth) {
      items.push({
        key: 'cancel',
        label: 'Cancel'
      });
    }
    return items;
  }, [isMdWidth]);

  const textModeLabel = useMemo(() => {
    const option = textModeOptions.find((option) => option?.key === textMode);
    return option && 'label' in option ? option.label : 'Transcript';
  }, [textMode]);

  return (
    <div className="media_viewer_transcript-extra">
      {isShowSearch && (
        <div className="media_viewer_transcript-drawer-search">
          <Input
            allowClear={{
              clearIcon: (
                <Row
                  onClick={(e) => e.stopPropagation()}
                  wrap={false}
                  gutter={8}
                  align="middle"
                >
                  <Col>2/10</Col>
                  <Col>
                    <Row>
                      <Col className="media_viewer_transcript-drawer-arrow">
                        <DownArrowSvg
                          className="b-white-icon"
                          width={18}
                          height={18}
                        />
                      </Col>
                      <Col
                        className="media_viewer_transcript-drawer-arrow"
                        style={{
                          transform: 'rotate(180deg)',
                          paddingTop: '2px',
                          alignItems: 'flex-start'
                        }}
                      >
                        <DownArrowSvg
                          className="b-white-icon"
                          width={18}
                          height={18}
                        />
                      </Col>
                    </Row>
                  </Col>
                  <Col>
                    <div className="b-line" />
                  </Col>
                  <Col>
                    <CrossSvg
                      onClick={() => {
                        setSearchValue('');
                        setIsShowSearch(false);
                      }}
                    />
                  </Col>
                </Row>
              )
            }}
            value={searchValue}
            onChange={(e) => {
              setSearchValue(e.target.value);
            }}
            autoFocus={true}
            prefix={<SearchSvg />}
            className="media_viewer_transcript-drawer-search-input"
            placeholder="Search transcript"
          />
        </div>
      )}
      <Row wrap={false} justify="space-between" gutter={8}>
        <Col flex="auto">
          <Dropdown
            menu={{
              selectable: true,
              selectedKeys: [textMode],
              onClick: ({ key }: { key: string }) => {
                if (key.indexOf('cancel') < 0) {
                  setTextMode(key as 'captions' | 'transcript');
                }
              },
              items: textModeOptions
            }}
            prefixCls="media_viewer_menu"
            overlayClassName="media_viewer_mobile-overlay"
            placement="bottom"
            trigger={['click']}
          >
            <Button className="media_viewer_btn" style={{ width: '100%' }}>
              {textModeLabel}
              <DownArrowSvg className="b-white-icon" />
            </Button>
          </Dropdown>
        </Col>
        <Col>
          <Button
            htmlType="button"
            onClick={() => {
              setIsShowSearch(true);
            }}
            className="media_viewer_btn b-only-icon"
          >
            <SearchSvg className="b-white-icon" />
          </Button>
        </Col>
      </Row>
    </div>
  );
}
